<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { adminApi } from '@/api/admin';
import type { SecretVersionDto, SecretVersionSummaryDto } from '@/api/types';
import { formatLocalDateTimeShort } from '@/lib/dateFormat';

// Per-slot secret version history. Opens as a right-side drawer over the SecretEditor. Lists
// every version newest-first (current included with an "active" badge), and lets the operator
// reveal a historical value and stage it back into the parent row's draft for a Save All
// commit. Restoring is implemented as a normal SetAsync of the old value rather than mutating
// history in place — the audit trail captures the rollback as a fresh write.
//
// Security note (rendered once per drawer open): rolling back to an older value writes it as
// the current secret. If the value was rotated due to suspected compromise, restoring may
// reintroduce the issue. Non-blocking; the operator can still Stage.
const props = defineProps<{
  visible: boolean;
  slot: string | null;
  scopeColonName: string | null;
}>();
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'stage', payload: { slot: string; scopeColonName: string; value: string; isTombstone: boolean }): void;
}>();

const versions = ref<SecretVersionSummaryDto[]>([]);
const loading = ref(false);
const loadError = ref<string | null>(null);
const selectedVersionId = ref<string | null>(null);
const selected = ref<SecretVersionDto | null>(null);
const selectedLoading = ref(false);

const refLabel = computed(() =>
  props.slot && props.scopeColonName ? `${props.slot} · ${props.scopeColonName}` : '');

watch(
  () => [props.visible, props.slot, props.scopeColonName] as const,
  async ([visible, slot, ref]) => {
    if (!visible || !slot || !ref) {
      versions.value = [];
      selectedVersionId.value = null;
      selected.value = null;
      loadError.value = null;
      return;
    }
    loading.value = true;
    loadError.value = null;
    try {
      versions.value = await adminApi.listSecretVersions(slot, ref);
    } catch (e) {
      loadError.value = (e as Error).message;
      versions.value = [];
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);

async function selectVersion(versionId: string) {
  if (!props.slot || !props.scopeColonName) return;
  selectedVersionId.value = versionId;
  selectedLoading.value = true;
  try {
    selected.value = await adminApi.getSecretVersion(props.slot, props.scopeColonName, versionId);
  } catch (e) {
    loadError.value = (e as Error).message;
    selected.value = null;
  } finally {
    selectedLoading.value = false;
  }
}

function stageSelected() {
  if (!props.slot || !props.scopeColonName || !selected.value) return;
  emit('stage', {
    slot: props.slot,
    scopeColonName: props.scopeColonName,
    value: selected.value.value,
    isTombstone: selected.value.isTombstone,
  });
  emit('update:visible', false);
}

function close() {
  emit('update:visible', false);
}

</script>

<template>
  <Drawer
    :visible="visible"
    position="right"
    :modal="true"
    class="history-drawer"
    @update:visible="(v: boolean) => emit('update:visible', v)"
  >
    <template #header>
      <div class="header">
        <i class="pi pi-history" />
        <span class="mono ref-label">{{ refLabel }}</span>
      </div>
    </template>

    <div class="content">
      <Message severity="warn" :closable="false" class="block-message">
        Rolling back to an older value writes it as the current secret. If this value was rotated
        because of a suspected compromise, restoring it may reintroduce the issue. Confirm via
        your rotation runbook before saving.
      </Message>

      <Message v-if="loadError" severity="error" :closable="false" class="block-message">
        {{ loadError }}
      </Message>

      <div v-if="loading" class="status">
        <ProgressSpinner style="width: 1.5rem; height: 1.5rem;" /> Loading versions…
      </div>
      <p v-else-if="versions.length === 0 && !loadError" class="status">
        No history at this slot.
      </p>

      <ul v-else class="versions">
        <li
          v-for="v in versions"
          :key="v.versionId"
          class="version-row"
          :class="{ selected: v.versionId === selectedVersionId }"
          @click="selectVersion(v.versionId)"
        >
          <div class="version-head">
            <span class="when mono">{{ formatLocalDateTimeShort(v.createdUtc) }}</span>
            <Tag
              v-if="v.isCurrent"
              value="current"
              severity="info"
              rounded
              class="small-tag"
            />
            <Tag
              v-if="v.isTombstone"
              value="→ null"
              severity="warn"
              rounded
              class="small-tag"
            />
          </div>
          <div class="version-meta">
            <span class="version-id mono">{{ v.versionId }}</span>
            <span class="author">{{ v.updatedBy ?? '—' }}</span>
          </div>
          <div v-if="v.versionId === selectedVersionId" class="version-reveal">
            <div v-if="selectedLoading" class="status">
              <ProgressSpinner style="width: 1rem; height: 1rem;" /> Loading value…
            </div>
            <template v-else-if="selected">
              <span v-if="selected.isTombstone" class="mono tombstone-pill">
                <i class="pi pi-ban" />&nbsp;→ null (overrides base)
              </span>
              <span v-else class="mono revealed-value">{{ selected.value }}</span>
            </template>
          </div>
        </li>
      </ul>
    </div>

    <template #footer>
      <div class="footer">
        <Button label="Close" severity="secondary" @click="close" />
        <Button
          label="Stage value"
          icon="pi pi-arrow-up"
          :disabled="!selected || selectedLoading"
          @click="stageSelected"
        />
      </div>
    </template>
  </Drawer>
</template>

<style scoped>
.history-drawer { width: 32rem; max-width: 90vw; }
.header { display: inline-flex; gap: 0.5rem; align-items: center; }
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
.ref-label { font-size: 0.875rem; color: var(--p-text-color, #111827); }
.content { display: flex; flex-direction: column; height: 100%; min-height: 0; gap: 0.5rem; }
.status { color: var(--p-text-muted-color, #6b7280); font-style: italic; padding: 0.5rem 0; display: flex; gap: 0.5rem; align-items: center; }
.block-message { margin: 0; }

.versions { list-style: none; margin: 0; padding: 0; overflow: auto; }
.version-row {
  padding: 0.6rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 4px;
  margin-bottom: 0.3rem;
  cursor: pointer;
}
.version-row:hover { background: var(--p-surface-100, #f3f4f6); }
.version-row.selected {
  background: var(--p-primary-50, #eff6ff);
  border-color: var(--p-primary-color, #2563eb);
}
.p-dark .version-row:hover {
  background: color-mix(in srgb, var(--p-primary-color) 12%, transparent);
}
.p-dark .version-row.selected {
  background: color-mix(in srgb, var(--p-primary-color) 28%, transparent);
}
.version-head { display: flex; align-items: center; gap: 0.4rem; }
.when { font-size: 0.8125rem; font-weight: 600; color: var(--p-text-color, #111827); }
.small-tag { font-size: 0.6875rem; }
.version-meta {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  margin-top: 0.2rem;
}
.version-id { font-size: 0.6875rem; opacity: 0.7; }
.author { font-style: italic; }
.version-reveal { margin-top: 0.4rem; padding: 0.4rem 0.5rem; background: var(--p-surface-50, #f9fafb); border-radius: 3px; }
/* `--p-surface-50` is a fixed light-scale value; in dark mode the drawer text flips to a
   light colour, so without this override the revealed value is light-on-light. Mirror the
   neutral-inset approach used for `.version-row` dark states (see the .p-dark rules above). */
.p-dark .version-reveal {
  background: color-mix(in srgb, var(--p-text-color) 8%, transparent);
}
.revealed-value { font-size: 0.8125rem; word-break: break-all; }
.tombstone-pill { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.8125rem; color: var(--p-text-muted-color, #6b7280); }

.footer { display: flex; gap: 0.5rem; justify-content: flex-end; }
</style>
